import { Component, OnInit } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms';
import { MediaService } from '../media.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Medium, MediumCategory, MediumEmoji, MediumFormat, MediumProduct, MediumRatingAgency } from '../medium.model';
import { FormMessage } from 'src/app/ui-design-library/form-message/form-message.model';
import { printPieceNumberValidator } from 'src/app/validators/custom-validators';
import { FormMessageComponent } from 'src/app/ui-design-library/form-message/form-message.component';
import { IconComponent } from 'src/app/ui-design-library/icon/icon.component';
import { ShowErrorComponent } from 'src/app/ui-design-library/forms/show-error/show-error.component';
import { InputFieldComponent } from 'src/app/ui-design-library/forms/input-field/input-field.component';
import { JsonPipe, NgClass } from '@angular/common';
import { AuthService } from 'src/app/auth/auth.service';
import { Observable } from 'rxjs';
import { finalize, take } from 'rxjs/operators';

@Component({
  selector: 'fnsr-medium-create',
  standalone: true,
  imports: [FormMessageComponent, IconComponent, ShowErrorComponent, ReactiveFormsModule, FormsModule, InputFieldComponent, NgClass, JsonPipe],
  templateUrl: './medium-form.component.html'
})
export class MediumFormComponent implements OnInit {
  form!: FormGroup;
  isLoading = false;
  formMessage?: FormMessage;

  medium$?: Observable<Medium>;
  mediumId?: string;
  originalValues: { [key: string]: any } = {};
  changedValues: any = {};

  isEditing = false;
  isAdmin = this.authService.isAdmin();

  formats = Object.values(MediumFormat);
  categories = Object.values(MediumCategory);
  emojis = Object.values(MediumEmoji);
  products = Object.values(MediumProduct);
  ratingAgencies = Object.values(MediumRatingAgency);

  constructor(
    private readonly fb: FormBuilder,
    private readonly authService: AuthService,
    private readonly mediaService: MediaService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.createForm();

    // Route-Parameter abfragen (prüfen, ob Medium bearbeitet wird)
    this.route.paramMap.subscribe(params => {
      this.mediumId = params.get('id') ?? undefined;
      if (this.mediumId) {
        this.loadMedium(this.mediumId);
      }

      this.form.valueChanges.subscribe((changes) => {
        Object.keys(changes).forEach(key => {
          if (this.form.get(key)?.dirty) {
            console.log(`Feld "${key}" geändert:`, changes[key]);
          }
        });
      });
    });

    // Medium aus Observable abrufen und ins Formular laden
    this.medium$?.pipe(take(1)).subscribe(medium => {
      if (medium) {
        this.originalValues = medium;
        this.form.patchValue(medium);
      }
    });
  }

  private createForm(): void {
    this.form = this.fb.group({
      title: this.fb.nonNullable.control('', [Validators.required]),
      subtitle: this.fb.nonNullable.control(''),
      format: this.fb.nonNullable.control('', [Validators.required]),
      category: this.fb.nonNullable.control(''),
      emoji: this.fb.nonNullable.control(''),
      mediumUrl: this.fb.nonNullable.control('', [Validators.required]),
      mediumThumbnailUrl: this.fb.nonNullable.control(''),
      printPieceNumber: this.fb.nonNullable.control('', printPieceNumberValidator),
      dateOfExpiry: this.fb.nonNullable.control(''),
      product: this.fb.nonNullable.control(''),
      ratingAgency: this.fb.nonNullable.control('')
    });
  }

  private loadMedium(id: string): void {
    this.isEditing = !!id; // Falls ID existiert, ist es eine Bearbeitung
    this.medium$ = this.mediaService.getMedium(id);
    this.medium$.subscribe(medium => {
      if (medium) {
        this.form.patchValue(medium);
      }
    });
  }
  
  // Prüft, ob sich ein Feldwert von seinem Original unterscheidet
  isFieldChanged(field: string): boolean {
    return this.form.controls[field].value !== this.originalValues[field];
  }

  getChangedValues() {
    this.changedValues = Object.fromEntries(
      Object.entries(this.form.value).filter(([key, value]) => this.originalValues && (this.originalValues as any)[key] !== value)
    );
    return this.changedValues;
}

  // Prüft, ob sich überhaupt etwas geändert hat
  isFormChanged(): boolean {
    const isFormChanged = Object.keys(this.form.value).some(field => this.isFieldChanged(field));
    if (isFormChanged === true) {
      this.formMessage = {
        message: `Es gibt ungespeicherte Änderungen`,
        messageType: 'info'
      };
    } else {
      this.formMessage = undefined;
    }
    return isFormChanged;
  }

  onSubmit(): void {
    if (this.form.invalid) return;

    this.isLoading = true;

    this.getChangedValues();

    const saveOperation = this.mediumId
      ? this.mediaService.updateMedium(this.mediumId, this.changedValues)
      : this.mediaService.postMedium(this.form.value);

    saveOperation.pipe(
      finalize(() => (this.isLoading = false))
    ).subscribe({
      next: result => {
        const successMessage = this.isEditing ? 'aktualisiert' : 'erstellt';
        this.formMessage = {
          message: `Erfolgreich ${successMessage}`,
          messageType: 'success'
        };
        this.mediumId = result?._id || this.mediumId;
        // TODO: Ein erneutes Laden aus der MongoDB erzwingen, damit
        // die Originalwerte aktualisiert werden
        this.router.navigate(['medium', this.mediumId]);
      },
      error: (err) => this.handleError(err, 'Speichern fehlgeschlagen. Bitte erneut versuchen.')
    });
  }

  deleteMedium(id: string): void {
    if (!id || !confirm(`Möchtest Du das Medium #${id} wirklich löschen?`)) {
      return;
    }

    this.mediaService.deleteMedium(id).subscribe({
      next: () => this.router.navigate(['medium/list']),
      error: (err) => this.handleError(err, 'Löschen fehlgeschlagen. Bitte erneut versuchen.')
    });
  }

  resetForm(): void {
    this.isLoading = false;
    this.formMessage = undefined;
    this.form.reset();
  }

  private handleError(error: any, message: string): void {
    console.error(message, error);
    this.formMessage = { messageType: 'error', message };
  }
}
